// WarningModal.jsx - Node 20 Version with z-index fix
import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import CircularProgress from "@mui/material/CircularProgress";
import { logoutUser } from "../../utils/authService";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1300,
    padding: "16px", // Add padding for mobile
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2, 3, 3),
    borderRadius: "15px",
    width: "90%", // Responsive width
    maxWidth: "400px", // Max width on desktop
    zIndex: 1301,
    position: "relative",
    boxSizing: "border-box",
  },
}));

export const RecordingConfirmationModal = ({ open, onClose, onConfirm }) => {
  const classes = useStyles();

  return (
    <Modal
      open={open}
      disableEnforceFocus={true}
      aria-labelledby="recording-modal-title"
      aria-describedby="recording-modal-description"
      className={classes.modal}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)', // ✅ Explicit backdrop color
            zIndex: 1300, // ✅ Backdrop z-index
          }
        },
      }}
      onClose={() => onClose(false)}
      sx={{
        zIndex: 1300, // ✅ Modal z-index
      }}
    >
      <Fade in={open}>
        <div className={classes.paper} style={{ textAlign: "justify" }}>
          <h3
            id="recording-modal-title"
            style={{
              color: "#6f2981",
              marginBottom: "30px",
              textAlign: "center",
            }}
          >
            Recording Limit
          </h3>
          <p id="recording-modal-description" style={{ marginBottom: "30px" }}>
            You have to record for{" "}
            <span style={{ color: "#6f2981" }}>
              <b>
                AT LEAST <u>30</u> seconds
              </b>
            </span>{" "}
            to successfully complete this question.
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              gap: "10px",
            }}
          >
            <button
              className="btn btn-blue"
              style={{ borderRadius: "10px" }}
              onClick={() => {
                onClose(false);
                onConfirm(true);
              }}
            >
              Proceed
            </button>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export const WarningModal = ({
  open,
  onClose,
  modalType,
  onRetry,
  isProcessing = false,
   _examId,
   _examDetail,
   _setWindowSwitch,
   _setTestData,
   _windowSwitchCount,
   _testData,
}) => {
  const classes = useStyles();
  const [lastModalType, setLastModalType] = useState(modalType);

  useEffect(() => {
    if (modalType) {
      setLastModalType(modalType);
    }
  }, [modalType]);

  const getTitle = () => {
    switch (lastModalType) {
      case "noAudio":
        return "No Audio Detected";
      case "noSpeech":
        return "No Speech Detected";
      case "highNoise":
        return "Excessive Background Noise";
      case "faceWarning":
        return "Face Detection Warning";
      case "faceNotDetected":
        return "Face Not Detected";
      case "faceNotMatching":
        return "Identity Verification Failed";
      case "multipleFaces":
        return "Multiple Faces Detected";
      case "identityWarning":
        return "Identity Verification Issue";
      case "shortDuration":
        return "Duration Too Short";
      case "corruptVideo":
        return "Video Upload Error";
      case "noFacialData":
        return "Face Not Checked During Recording";
      case "permissionDenied":
        return "Microphone Access Denied";
      case "permissionDeniediOS":
        return "Microphone Access Denied on iOS";
      case "encodingError":
        return "Audio Processing Error";
      case "safariWarning":
        return "Browser Tip";
      case "databaseError":
        return "Database Error";
      case "noMediaDevice":
        return "Media Device Not Found";
      default:
        return "Warning";
    }
  };

  const getMessage = () => {
    switch (lastModalType) {
      case "noAudio":
        return "No audio was detected in your recording. Please ensure your microphone is working and try again.";
      case "noSpeech":
        return "No human speech was detected in your recording. Please ensure you are speaking clearly and try again.";
      case "highNoise":
        return "There is too much background noise in your recording. Please find a quieter environment and try again.";
      case "faceWarning":
        return "No face was detected during the recording. Please ensure you are visible in the camera frame and try again.";
      case "faceNotDetected":
        return "We couldn't detect your face in the camera frame. Please ensure your face is clearly visible and try again.";
      case "faceNotMatching":
        return "Your face doesn't match with your registered profile. Please ensure proper lighting and positioning, then try again.";
      case "multipleFaces":
        return "Multiple faces were detected in the recording. Please ensure only you are visible in the camera frame and try again.";
      case "identityWarning":
        return "There seems to be an issue verifying your identity. Please make sure your face is clearly visible and matches your profile photo.";
      case "noFacialData":
        // Shown when no face check finished during the recording - not about its length (a 30 s+
        // answer got this and read as "video too short", staging 27 Sep 2026).
        return "We couldn't check your face while you were recording. Please make sure your face is clearly visible in the camera and record your answer again.";
      case "shortDuration":
        return (
          <>
            Your recording should be{" "}
            <span style={{ color: "#6f2981" }}>
              <b>
                AT LEAST <u>30</u> seconds
              </b>
            </span>{" "}
            long to move forward.
          </>
        );
      case "corruptVideo":
        return "The uploaded video appears to be corrupted. Please re-record the video.";
      case "permissionDenied":
        return "Microphone access was denied. Please allow microphone access in your browser settings and try again, or use a different device.";
      case "permissionDeniediOS":
        return (
          <>
            Microphone access was denied on your iOS device. Please go to <b>Settings &gt; Safari &gt; Microphone</b> and allow microphone access, or
            use a different device.
          </>
        );
      case "encodingError":
        return "There was an issue processing the audio. Please ensure your microphone is working and try again, or use a different device.";
      case "safariWarning":
        return (
          <>
            If you face any issues during recording, try switching to a{" "}
            <b>different browser</b>. Some iPhones work better with{" "}
            <b>Chrome</b>, while others work better with <b>Safari</b>.
            <br /><br />
            You can continue now - just switch browsers if you encounter problems.
          </>
        );
      case "databaseError":
        return "There was an error saving your video to the database. Please try recording again or contact support if the issue persists.";
      case "noMediaDevice":
        return "No camera or microphone was found on this device. Please ensure your devices are connected and permissions are granted.";
      default:
        return "An unexpected error occurred. Please try again or contact support if the issue persists.";
    }
  };

  const handleLogout = async () => {
    const isSigninFlow = typeof window !== "undefined" && window.location.pathname.includes("/signin");
    const redirectUrl = isSigninFlow ? '/v2/user/signin' : '/v2/user/login';
    window.onbeforeunload = null;
    try {
      await logoutUser();
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      sessionStorage.clear();
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 0);
    }
  };

  const handleBeforeUnload = (event) => {
    if (lastModalType !== "safariWarning") {
      event.preventDefault();
      event.returnValue = "";
    }
  };

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastModalType]);

  const handleModalClose = () => {
    // For safariWarning, just close the modal and let user continue
    onClose();
  };

  const handleRetry = () => {
    // For safariWarning, just close and let user continue
    if (lastModalType === "safariWarning") {
      onClose();
    } else {
      onRetry();
    }
  };

  return (
    <Modal
      open={open}
      disableEnforceFocus={true}
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)', // ✅ Explicit backdrop color
            zIndex: 1300, // ✅ Backdrop z-index
          }
        },
      }}
      onClose={handleModalClose}
      sx={{
        zIndex: 1300, // ✅ Modal z-index
      }}
    >
      <Fade in={open}>
        <div className={classes.paper} style={{ textAlign: "justify" }}>
          <h3
            id="transition-modal-title"
            style={{
              color: "#6f2981",
              marginBottom: "30px",
              textAlign: "center",
            }}
          >
            {getTitle()}
          </h3>
          <p id="transition-modal-description" style={{ marginBottom: "30px" }}>
            {getMessage()}
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <button
              className="btn btn-blue"
              style={{ 
                borderRadius: "10px",
                minWidth: "140px",
                padding: "12px 20px",
                fontSize: "14px",
                whiteSpace: "nowrap",
              }}
              onClick={handleRetry}
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : (lastModalType === "safariWarning" ? "Continue" : "Retry")}
            </button>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export const VideoUploadingModal = ({ open, progress }) => {
  const classes = useStyles();
  const [messageIndex, setMessageIndex] = useState(0);

  const messages = [
    "Please wait your video is being uploaded...",
    "Use Wi-Fi or high-speed internet for faster upload.",
    "A slow internet connection may cause delays in uploading your video.",
  ];

  useEffect(() => {
    if (!open) return;

    const intervalId = setInterval(() => {
      setMessageIndex((prevIndex) => (prevIndex + 1) % messages.length);
    }, 3000);

    return () => clearInterval(intervalId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const showProgress = typeof progress === "number" && progress > 0;

  return (
    <Modal
      open={open}
      disableEnforceFocus={true}
      aria-labelledby="uploading-modal-title"
      aria-describedby="uploading-modal-description"
      className={classes.modal}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1400,
          }
        },
      }}
      sx={{
        zIndex: 1400,
      }}
    >
      <Fade in={open}>
        <div className={classes.paper} style={{ textAlign: "center" }}>
          <h3
            id="recording-modal-title"
            style={{
              color: "#6f2981",
              marginBottom: "20px",
              textAlign: "center",
            }}
          >
            Uploading Your Video
          </h3>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
            {showProgress ? (
              <CircularProgress variant="determinate" value={progress} style={{ color: "#6f2981" }} />
            ) : (
              <CircularProgress style={{ color: "#6f2981" }} />
            )}
          </div>
          {showProgress && (
            <p style={{ color: "#6f2981", fontWeight: "bold", marginBottom: "8px" }}>
              {progress < 100 ? `${progress}%` : "Saving..."}
            </p>
          )}
          <p
            id="recording-modal-description"
            style={{
              marginBottom: "20px",
              minHeight: "40px",
            }}
          >
            {messages[messageIndex]}
          </p>
        </div>
      </Fade>
    </Modal>
  );
};

export const VideoProcessingModal = ({ open }) => {
  const classes = useStyles();
  const [messageIndex, setMessageIndex] = useState(0);

  const messages = [
    "Please wait, your video is being processed...",
    "This may take a moment depending on the video size.",
    "Ensure a stable internet connection for faster processing.",
  ];

  useEffect(() => {
    if (!open) return;

    const intervalId = setInterval(() => {
      setMessageIndex((prevIndex) => (prevIndex + 1) % messages.length);
    }, 3000);

    return () => clearInterval(intervalId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      open={open}
      disableEnforceFocus={true}
      aria-labelledby="processing-modal-title"
      aria-describedby="processing-modal-description"
      className={classes.modal}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1400,  // Higher than proctoring modals to prevent overlap
          }
        },
      }}
      sx={{
        zIndex: 1400,  // Higher than proctoring modals to prevent overlap
      }}
    >
      <Fade in={open}>
        <div className={classes.paper} style={{ textAlign: "center" }}>
          <h3
            id="processing-modal-title"
            style={{
              color: "#6f2981",
              marginBottom: "20px",
              textAlign: "center",
            }}
          >
            Processing Your Video
          </h3>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
            <CircularProgress style={{ color: "#6f2981" }} />
          </div>
          <p
            id="processing-modal-description"
            style={{
              marginBottom: "20px",
              minHeight: "40px",
            }}
          >
            {messages[messageIndex]}
          </p>
        </div>
      </Fade>
    </Modal>
  );
};

export const AudioUploadingModal = ({ open }) => {
  const classes = useStyles();
  const [messageIndex, setMessageIndex] = useState(0);

  const messages = [
    "Please wait your audio is being uploaded...",
    "Use Wi-Fi or high-speed internet for faster upload.",
    "A slow internet connection may cause delays in uploading your audio.",
  ];

  useEffect(() => {
    if (!open) return;

    const intervalId = setInterval(() => {
      setMessageIndex((prevIndex) => (prevIndex + 1) % messages.length);
    }, 3000);

    return () => clearInterval(intervalId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      open={open}
      disableEnforceFocus={true}
      aria-labelledby="uploading-modal-title"
      aria-describedby="uploading-modal-description"
      className={classes.modal}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1300,
          }
        },
      }}
      sx={{
        zIndex: 1300,
      }}
    >
      <Fade in={open}>
        <div className={classes.paper} style={{ textAlign: "center" }}>
          <h3
            id="uploading-modal-title"
            style={{
              color: "#6f2981",
              marginBottom: "20px",
              textAlign: "center",
            }}
          >
            Uploading Your Audio
          </h3>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
            <CircularProgress style={{ color: "#6f2981" }} />
          </div>
          <p
            id="uploading-modal-description"
            style={{
              marginBottom: "20px",
              minHeight: "40px",
            }}
          >
            {messages[messageIndex]}
          </p>
        </div>
      </Fade>
    </Modal>
  );
};
